import { Offer, Settings } from '../../../domain';
import { logError } from '../../../utils/logging';
import { GeneralTranslations, OfferTranslations } from '../types';
import { getTranslationsMetaobjectById } from './getTranslationsMetaobject';
import { getTranslatedValue } from './getTranslatedValue';
import { getProductCollectionByHandle } from './getProductCollection';

/**
 * Get translations for a specific offer using its metaobject ID
 * @param storefrontAccessToken - Storefront access token
 * @param metaobjectId - The specific metaobject ID to fetch translations from
 * @param offer - The offer to get translations for
 * @returns Promise<Translations> - The translations object
 */
export const getTranslationsForOfferById = async (
  storefrontAccessToken: string,
  offer: Offer,
  generalMetaobject: Record<string, string> | null,
  settings: Settings,
  metaobjectId?: number,
): Promise<OfferTranslations> => {
  let translationsMetaobjectData: Record<string, string> | null = null;
  let productCollectionData: Record<string, string> | null = null;

  if (storefrontAccessToken !== '') {
    if (metaobjectId) {
      try {
        translationsMetaobjectData = await getTranslationsMetaobjectById(
          storefrontAccessToken,
          metaobjectId,
        );
      } catch (error) {
        // Do not stop the app if the translations are not fetched.
        // It may happen if the metaobject doesn't exist or permissions haven't been granted yet.
        logError(error);
      }
    }

    if (offer.target_shopify_collection_handle) {
      try {
        productCollectionData = await getProductCollectionByHandle(
          storefrontAccessToken,
          offer.target_shopify_collection_handle,
        );
      } catch (error) {
        logError(error);
      }
    }
  }

  const translate = (key: string, fallback?: string) =>
    getTranslatedValue(
      key,
      translationsMetaobjectData,
      generalMetaobject,
      offer,
      fallback,
      'Offer',
    );

  return {
    name: translate('name'),
    display_name:
      translationsMetaobjectData?.display_name || offer.display_name || '',
    subtitle_condition_met_product_selection: translate(
      'subtitle_condition_met_product_selection',
    ),
    subtitle_condition_not_met: translate('subtitle_condition_not_met'),
    title_condition_met_product_selection: translate(
      'title_condition_met_product_selection',
    ),
    title_condition_not_met: translate('title_condition_not_met'),
    title_empty_cart: translate('title_empty_cart'),
    and_select_one_of_the_free_gifts: translate(
      'and_select_one_of_the_free_gifts',
    ),
    buy_any_product_from_the_collection:
      translationsMetaobjectData?.buy_any_product_from_the_collection ||
      generalMetaobject?.buy_any_product_from_the_collection ||
      offer.title_condition_not_met ||
      settings.buy_any_product_from_the_collection ||
      'Buy any product from the collection',
    buy_product:
      translationsMetaobjectData?.buy_product ||
      generalMetaobject?.buy_product ||
      offer.title_condition_not_met ||
      settings.buy_product ||
      'Buy product',
    buy_reqproductq_products:
      translationsMetaobjectData?.buy_reqproductq_products ||
      generalMetaobject?.buy_reqproductq_products ||
      offer.title_condition_not_met ||
      settings.buy_reqproductq_products ||
      'Buy REQPRODUCTQ× product',
    buy_reqproductq_products_from_the_collection:
      translationsMetaobjectData?.buy_reqproductq_products_from_the_collection ||
      generalMetaobject?.buy_reqproductq_products_from_the_collection ||
      offer.title_condition_not_met ||
      settings.buy_reqproductq_products_from_the_collection ||
      'Buy REQPRODUCTQ× products from the collection',
    custom_free_gift:
      translationsMetaobjectData?.custom_free_gift ||
      generalMetaobject?.custom_free_gift ||
      offer.display_name ||
      settings.custom_free_gift ||
      'Custom free gift',
    dont_miss_your_reward:
      translationsMetaobjectData?.dont_miss_your_reward ||
      generalMetaobject?.dont_miss_your_reward ||
      offer.subtitle_condition_met_product_selection ||
      settings.dont_miss_your_reward ||
      "Don't miss your reward",
    free_shipping:
      translationsMetaobjectData?.free_shipping ||
      generalMetaobject?.free_shipping ||
      offer.display_name ||
      'Free shipping',
    select_your_free_gift:
      translationsMetaobjectData?.select_your_free_gift ||
      generalMetaobject?.select_your_free_gift ||
      offer.title_condition_met_product_selection ||
      settings.select_your_free_gift ||
      'Select your free gift',
    spend_remainingspend:
      translationsMetaobjectData?.spend_remainingspend ||
      generalMetaobject?.spend_remainingspend ||
      offer.title_empty_cart ||
      settings.spend_remainingspend ||
      'Spend REMAININGSPEND',
    spend_remainingspend_in_the_collection:
      translationsMetaobjectData?.spend_remainingspend_in_the_collection ||
      generalMetaobject?.spend_remainingspend_in_the_collection ||
      offer.title_empty_cart ||
      settings.spend_remainingspend_in_the_collection ||
      'Spend REMAININGSPEND in the collection',
    spend_remainingspend_more:
      translationsMetaobjectData?.spend_remainingspend_more ||
      generalMetaobject?.spend_remainingspend_more ||
      offer.title_condition_not_met ||
      settings.spend_remainingspend_more ||
      'Spend only REMAININGSPEND more',
    spend_remainingspend_more_in_the_collection:
      translationsMetaobjectData?.spend_remainingspend_more_in_the_collection ||
      generalMetaobject?.spend_remainingspend_more_in_the_collection ||
      offer.title_condition_not_met ||
      settings.spend_remainingspend_more_in_the_collection ||
      'Spend only REMAININGSPEND more in the collection',
    to_get_a_free_displayname: translate('to_get_a_free_displayname'),
    to_get_a_free_gift: translate('to_get_a_free_gift'),
    to_get_free_shipping:
      translationsMetaobjectData?.to_get_free_shipping ||
      generalMetaobject?.to_get_free_shipping ||
      offer.subtitle_condition_not_met ||
      settings.to_get_free_shipping ||
      'to get free shipping',
    target_shopify_collection_name:
      productCollectionData?.title ||
      offer.target_shopify_collection_name ||
      '',
  };
};
